/**
 * Form components and utilities for the Hydra application.
 *
 * This module provides a wrapper around Pluma form components with TanStack form integration,
 * offering type-safe form handling and field management.
 */

import {
  Form as PlumaForm,
  TextField as PlumaTextField,
  TextArea as PlumaTextArea,
  Select as PlumaSelect,
  Slider as PlumaSlider,
} from '@customerio/pluma-components/react';
import { createFormHook, createFormHookContexts } from '@tanstack/react-form';
import type { ComponentProps } from 'react';

import type { ReactFormExtendedApi, FieldApi } from '@tanstack/react-form';
import type { FormValidateOrFn, FormAsyncValidateOrFn } from '@tanstack/form-core';

interface TanstackFormApiProp<
  TFormData,
  _TOnMount extends undefined | FormValidateOrFn<TFormData>,
  _TOnChange extends undefined | FormValidateOrFn<TFormData>,
  _TOnChangeAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  _TOnBlur extends undefined | FormValidateOrFn<TFormData>,
  _TOnBlurAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  _TOnSubmit extends undefined | FormValidateOrFn<TFormData>,
  _TOnSubmitAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  _TOnServer extends undefined | FormAsyncValidateOrFn<TFormData>,
  _TSubmitMeta = never,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  FormApi extends ReactFormExtendedApi<
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any
  > = ReactFormExtendedApi<any, any, any, any, any, any, any, any, any, any, any, any>,
> {
  api: FormApi;
}

/**
 * Form and field contexts for managing form state across components.
 */
export const { fieldContext, formContext, useFieldContext } = createFormHookContexts();

/**
 * Custom form hook with type-safe form handling.
 */
export const { useAppForm: useForm, withForm } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
});

/**
 * Form component that wraps Pluma's Form with TanStack form integration.
 *
 * Automagically handles form submission and integrates with TanStack form API.
 */
export function Form<
  TFormData,
  TOnMount extends undefined | FormValidateOrFn<TFormData>,
  TOnChange extends undefined | FormValidateOrFn<TFormData>,
  TOnChangeAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnBlur extends undefined | FormValidateOrFn<TFormData>,
  TOnBlurAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnSubmit extends undefined | FormValidateOrFn<TFormData>,
  TOnSubmitAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnServer extends undefined | FormAsyncValidateOrFn<TFormData>,
  TSubmitMeta = never,
>({
  api,
  ...rest
}: TanstackFormApiProp<
  TFormData,
  TOnMount,
  TOnChange,
  TOnChangeAsync,
  TOnBlur,
  TOnBlurAsync,
  TOnSubmit,
  TOnSubmitAsync,
  TOnServer,
  TSubmitMeta
> &
  ComponentProps<typeof PlumaForm>) {
  return (
    <PlumaForm
      onSubmit={(event) => {
        event.preventDefault();
        if (api !== undefined) {
          return api.handleSubmit();
        }
      }}
      {...rest}
    />
  );
}

/**
 * TextField component that integrates Pluma TextField with TanStack form.
 * Supports both controlled (with api) and uncontrolled usage.
 */
export function TextField<
  TFormData,
  TOnMount extends undefined | FormValidateOrFn<TFormData>,
  TOnChange extends undefined | FormValidateOrFn<TFormData>,
  TOnChangeAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnBlur extends undefined | FormValidateOrFn<TFormData>,
  TOnBlurAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnSubmit extends undefined | FormValidateOrFn<TFormData>,
  TOnSubmitAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnServer extends undefined | FormAsyncValidateOrFn<TFormData>,
  TSubmitMeta = never,
>({
  name,
  api,
  onInput,
  onBlur,
  ...rest
}: TanstackFormApiProp<
  TFormData,
  TOnMount,
  TOnChange,
  TOnChangeAsync,
  TOnBlur,
  TOnBlurAsync,
  TOnSubmit,
  TOnSubmitAsync,
  TOnServer,
  TSubmitMeta
> & {
  name?: keyof TFormData;
} & ComponentProps<typeof PlumaTextField>) {
  if (api === undefined) {
    return <PlumaTextField name={name} {...rest} />;
  }

  return (
    <api.Field name={name as keyof TFormData & string}>
      {(
        field: FieldApi<
          TFormData,
          keyof TFormData & string,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any
        >,
      ) => (
        <PlumaTextField
          value={field.state.value as string}
          onInput={(event) => {
            field.handleChange(event.target.value);
            onInput?.(event);
          }}
          onBlur={(event) => {
            field.handleBlur();
            onBlur?.(event);
          }}
          name={field.name}
          error={field?.state?.meta?.errors?.[0]?.message}
          {...rest}
        />
      )}
    </api.Field>
  );
}

/**
 * TextArea component that integrates Pluma TextArea with TanStack form.
 * Supports both controlled (with api) and uncontrolled usage.
 */
export function TextArea<
  TFormData,
  TOnMount extends undefined | FormValidateOrFn<TFormData>,
  TOnChange extends undefined | FormValidateOrFn<TFormData>,
  TOnChangeAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnBlur extends undefined | FormValidateOrFn<TFormData>,
  TOnBlurAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnSubmit extends undefined | FormValidateOrFn<TFormData>,
  TOnSubmitAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnServer extends undefined | FormAsyncValidateOrFn<TFormData>,
  TSubmitMeta = never,
>({
  name,
  api,
  onInput,
  onBlur,
  ...rest
}: TanstackFormApiProp<
  TFormData,
  TOnMount,
  TOnChange,
  TOnChangeAsync,
  TOnBlur,
  TOnBlurAsync,
  TOnSubmit,
  TOnSubmitAsync,
  TOnServer,
  TSubmitMeta
> & {
  name?: keyof TFormData;
} & ComponentProps<typeof PlumaTextArea>) {
  if (api === undefined) {
    return <PlumaTextArea name={name} {...rest} />;
  }

  return (
    <api.Field name={name as keyof TFormData & string}>
      {(
        field: FieldApi<
          TFormData,
          keyof TFormData & string,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any
        >,
      ) => (
        <PlumaTextArea
          value={field.state.value as string}
          onInput={(event) => {
            field.handleChange(event.target.value);
            onInput?.(event);
          }}
          onBlur={(event) => {
            field.handleBlur();
            onBlur?.(event);
          }}
          name={field.name}
          error={field?.state?.meta?.errors?.[0]?.message}
          {...rest}
        />
      )}
    </api.Field>
  );
}

/**
 * Select component that integrates Pluma Select with TanStack form.
 * Supports both controlled (with api) and uncontrolled usage.
 */
export function Select<
  TFormData,
  TOnMount extends undefined | FormValidateOrFn<TFormData>,
  TOnChange extends undefined | FormValidateOrFn<TFormData>,
  TOnChangeAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnBlur extends undefined | FormValidateOrFn<TFormData>,
  TOnBlurAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnSubmit extends undefined | FormValidateOrFn<TFormData>,
  TOnSubmitAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnServer extends undefined | FormAsyncValidateOrFn<TFormData>,
  TSubmitMeta = never,
>({
  name,
  api,
  onChange,
  onBlur,
  ...rest
}: Partial<
  TanstackFormApiProp<
    TFormData,
    TOnMount,
    TOnChange,
    TOnChangeAsync,
    TOnBlur,
    TOnBlurAsync,
    TOnSubmit,
    TOnSubmitAsync,
    TOnServer,
    TSubmitMeta
  >
> & {
  name?: keyof TFormData;
} & ComponentProps<typeof PlumaSelect>) {
  if (api === undefined) {
    return <PlumaSelect name={name} onChange={onChange as any} {...rest} />;
  }

  return (
    <api.Field name={name as keyof TFormData & string}>
      {(
        field: FieldApi<
          TFormData,
          keyof TFormData & string,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any
        >,
      ) => (
        <PlumaSelect
          value={field.state.value as any}
          onChange={(value) => {
            field.handleChange(value as unknown);
            onChange?.(value as any);
          }}
          onBlur={(event) => {
            field.handleBlur();
            onBlur?.(event);
          }}
          name={field.name}
          error={field?.state?.meta?.errors?.[0]?.message}
          {...rest}
        />
      )}
    </api.Field>
  );
}

/**
 * Slider component that integrates Pluma Slider with TanStack form.
 * Supports both controlled (with api) and uncontrolled usage.
 */
export function Slider<
  TFormData,
  TOnMount extends undefined | FormValidateOrFn<TFormData>,
  TOnChange extends undefined | FormValidateOrFn<TFormData>,
  TOnChangeAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnBlur extends undefined | FormValidateOrFn<TFormData>,
  TOnBlurAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnSubmit extends undefined | FormValidateOrFn<TFormData>,
  TOnSubmitAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnServer extends undefined | FormAsyncValidateOrFn<TFormData>,
  TSubmitMeta = never,
>({
  name,
  api,
  onInput,
  onChange,
  ...rest
}: TanstackFormApiProp<
  TFormData,
  TOnMount,
  TOnChange,
  TOnChangeAsync,
  TOnBlur,
  TOnBlurAsync,
  TOnSubmit,
  TOnSubmitAsync,
  TOnServer,
  TSubmitMeta
> & {
  name?: keyof TFormData;
} & ComponentProps<typeof PlumaSlider>) {
  if (api === undefined) {
    return <PlumaSlider name={name} onInput={onInput} onChange={onChange} {...rest} />;
  }

  return (
    <api.Field name={name as keyof TFormData & string}>
      {(
        field: FieldApi<
          TFormData,
          keyof TFormData & string,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any,
          any
        >,
      ) => (
        <PlumaSlider
          value={field.state.value as number}
          onInput={(value) => {
            field.handleChange(value);
            onInput?.(value);
          }}
          onChange={(value) => {
            field.handleChange(value);
            onChange?.(value);
          }}
          name={field.name}
          error={field?.state?.meta?.errors?.[0]?.message}
          {...rest}
        />
      )}
    </api.Field>
  );
}
